/**
 * @file raceResultBridge — TEMPORARY browser-side stub for the hot-lap
 * submission backend.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS (read before editing)
 * ─────────────────────────────────────────────────────────────────────────────
 *  The Sky City Rush WebGL build calls a JavaScript bridge when a race ends.
 *  The real leaderboard backend does not exist yet, so this module makes the web
 *  page *pretend* to be that backend. From Unity's point of view nothing is
 *  different: it calls the same global function, gets a Promise back, and
 *  receives a normal success response.
 *
 *  The Unity build, its WebGL output, the .jslib plugin, CompetitiveManager and
 *  all race logic are UNCHANGED. This file is the only thing standing in for the
 *  server.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  EXACT CONTRACT (reverse-engineered from the shipped build, do not break it)
 * ─────────────────────────────────────────────────────────────────────────────
 *  The build's .jslib does, in effect:
 *
 *      window.SubmitRaceResult(
 *        gameId, trackId, vehicleId, modeId,
 *        lapCount, timeMs, gameVersion, completedAt
 *      )
 *      .then(result => {
 *        window.UnityBridge.sendMessage(
 *          "CompetitiveManager", "OnRunSubmitted", JSON.stringify(result)
 *        );
 *      })
 *      .catch(error => { ...sendMessage(... {success:false, error}) });
 *
 *  Therefore:
 *    • window.SubmitRaceResult MUST return a Promise (the build calls .then).
 *    • It is called with EIGHT positional arguments (not a JSON string/object).
 *      We still parse defensively in case a future build passes a string/object.
 *    • The resolved value MUST be a plain OBJECT — the build serializes it with
 *      JSON.stringify before handing it to Unity. Returning a pre-serialized
 *      string here would double-encode it and break CompetitiveManager.
 *    • window.UnityBridge.sendMessage must exist too; the build only ever READS
 *      window.UnityBridge, it never creates it, so we supply it here and forward
 *      to the live Unity instance's SendMessage.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  REPLACING THE STUB WITH THE REAL BACKEND (future)
 * ─────────────────────────────────────────────────────────────────────────────
 *  When the backend ships it will expose roughly:  POST /api/hotlap/submit
 *  Only `submitToBackend()` below needs to change — swap its stub body for a
 *  fetch() call (a commented template is included). Everything else — the global
 *  registration, argument parsing, logging, error handling — stays exactly as
 *  is. That is the whole point of this structure.
 */

/** Intended real endpoint. Not called yet — documented for the swap-in. */
export const HOTLAP_SUBMIT_ENDPOINT = '/api/hotlap/submit';

/** Simulated network latency so the stub feels like a real request. */
const SIMULATED_LATENCY_MS = 500;

/**
 * @typedef {Object} HotLapSubmission
 * @property {string} [gameId]
 * @property {string} [trackId]
 * @property {string} [vehicleId]
 * @property {string} [modeId]
 * @property {number} [lapCount]
 * @property {number} [timeMs]
 * @property {string} [gameVersion]
 * @property {string} [completedAt]
 * @property {*} [*]                 Tolerated extra/unknown fields.
 */

/**
 * @typedef {Object} HotLapResponse   Shape the real backend is expected to send.
 * @property {boolean} success
 * @property {boolean} accepted
 * @property {string|null} submissionId
 * @property {number|null} leaderboardPosition
 * @property {boolean} personalBest
 * @property {boolean} newRecord
 * @property {string} message
 */

/* ───────────────────────── Unity instance plumbing ───────────────────────── */

/** The live Unity instance (from createUnityInstance). Set once the game boots. */
let unityInstance = null;

/* ─────────────────────────── archived-week mode ──────────────────────────── */

/**
 * When an ARCHIVED week is being played there is no live competition: no
 * leaderboard, no ranking, no submission, no backend interaction. We detect this
 * purely in the web layer (the page tells us) and short-circuit submissions.
 */
let archivedMode = false;
/** @type {((info: { payload: HotLapSubmission }) => void) | null} */
let archivedNoticeHandler = null;

/**
 * Toggle archived-week mode. In archived mode SubmitRaceResult does not submit;
 * it returns an inert "archived" response and notifies the page so it can show
 * "This week's leaderboard is no longer active."
 * @param {boolean} isArchived
 */
export function setArchivedMode(isArchived) {
  archivedMode = Boolean(isArchived);
}

/** Whether archived mode is currently active. */
export function isArchivedMode() {
  return archivedMode;
}

/**
 * Register a callback fired when a race finishes while playing an archived week
 * (used by the page to reveal the archive notice overlay).
 * @param {((info: { payload: HotLapSubmission }) => void) | null} handler
 */
export function setArchivedNoticeHandler(handler) {
  archivedNoticeHandler = typeof handler === 'function' ? handler : null;
}

/**
 * Register the running Unity instance so window.UnityBridge can forward messages
 * back into the game. Called from the player's onReady handler.
 * @param {*} instance
 */
export function setUnityInstance(instance) {
  unityInstance = instance || null;
}

/**
 * window.UnityBridge.sendMessage implementation. Forwards to the Unity instance
 * and NEVER throws (a broken bridge must not surface a JS exception in Unity).
 * @param {string} objectName
 * @param {string} methodName
 * @param {string} value
 */
function unityBridgeSendMessage(objectName, methodName, value) {
  try {
    if (unityInstance && typeof unityInstance.SendMessage === 'function') {
      unityInstance.SendMessage(objectName, methodName, value);
    } else {
      // Not fatal: the game finished a race before the instance was registered,
      // or is being torn down. Log so we can see it during development.
      console.warn(
        '[SCR Weekly] UnityBridge.sendMessage called before Unity was ready:',
        { objectName, methodName, value }
      );
    }
  } catch (err) {
    console.error('[SCR Weekly] UnityBridge.sendMessage failed:', err);
  }
}

/* ────────────────────────── payload normalization ────────────────────────── */

/**
 * Normalize whatever the caller passed into a single submission object.
 * Handles the real 8-positional-argument contract, and defensively also a
 * single JSON string or a single object (requirement: do not assume one format).
 *
 * @param {IArguments|Array<*>} args
 * @returns {HotLapSubmission}
 */
function normalizeSubmission(args) {
  const list = Array.prototype.slice.call(args);

  if (list.length === 1) {
    const only = list[0];
    if (typeof only === 'string') {
      try {
        const parsed = JSON.parse(only);
        if (parsed && typeof parsed === 'object') return parsed;
      } catch (e) {
        /* not JSON — fall through and keep the raw string */
      }
      return { raw: only };
    }
    if (only && typeof only === 'object') {
      return { ...only };
    }
  }

  // Real contract: 8 positional arguments.
  const [
    gameId,
    trackId,
    vehicleId,
    modeId,
    lapCount,
    timeMs,
    gameVersion,
    completedAt,
  ] = list;

  return {
    gameId,
    trackId,
    vehicleId,
    modeId,
    lapCount,
    timeMs,
    gameVersion,
    completedAt,
  };
}

/** Pick the first defined value among candidate keys, else a dash. */
function firstOf(obj, keys) {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return '—';
}

/**
 * Log a readable summary of the submission for debugging during development.
 * @param {HotLapSubmission} payload
 */
function logSubmission(payload) {
  const line = '========================================';
  /* eslint-disable no-console */
  console.log(
    [
      line,
      'SCR Weekly Stub Submission',
      `Season:      ${firstOf(payload, ['season'])}`,
      `Week:        ${firstOf(payload, ['week'])}`,
      `Competition: ${firstOf(payload, ['competition', 'gameId'])}`,
      `Player:      ${firstOf(payload, ['player', 'playerId'])}`,
      `Vehicle:     ${firstOf(payload, ['vehicle', 'vehicleId'])}`,
      `Lap:         ${firstOf(payload, ['lap', 'lapCount'])}`,
      `Time:        ${firstOf(payload, ['time', 'timeMs'])}`,
      'Full Payload:',
    ].join('\n')
  );
  console.log(payload);
  console.log(line);
  /* eslint-enable no-console */
}

/* ─────────────────────────── response builders ──────────────────────────── */

/**
 * Build a realistic success response matching the future backend's shape.
 * @param {HotLapSubmission} _payload  Available for future logic (unused now).
 * @returns {HotLapResponse}
 */
function buildStubResponse(_payload) {
  const submissionId = `stub-${Date.now().toString(36)}${Math.floor(
    Math.random() * 1000
  )
    .toString()
    .padStart(3, '0')}`;

  return {
    success: true,
    accepted: true,
    submissionId,
    leaderboardPosition: 1 + Math.floor(Math.random() * 25),
    personalBest: true,
    newRecord: false,
    message: 'Hot lap submitted successfully.',
  };
}

/**
 * Build the inert response used when an archived week is played. It is a valid,
 * successful-looking response (so Unity's flow proceeds without errors) but it
 * accepts nothing and carries no ranking — there is no live leaderboard.
 * @returns {HotLapResponse}
 */
function buildArchivedResponse() {
  return {
    success: true,
    accepted: false,
    submissionId: null,
    leaderboardPosition: null,
    personalBest: false,
    newRecord: false,
    message: "Archive challenge — this week's leaderboard is no longer active.",
  };
}

/**
 * Build a valid failure response. Returned (not thrown) so Unity always gets a
 * well-formed object.
 * @param {string} message
 * @returns {HotLapResponse}
 */
function buildFailureResponse(message) {
  return {
    success: false,
    accepted: false,
    submissionId: null,
    leaderboardPosition: null,
    personalBest: false,
    newRecord: false,
    message: message || 'Submission could not be processed.',
  };
}

/** @param {number} ms @returns {Promise<void>} */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/* ───────────────────────── the integration point ────────────────────────── */

/**
 * THE ONE FUNCTION THAT CHANGES when the backend exists.
 *
 * Today: waits ~500ms and returns a stub response.
 * Later: replace the body with a real request, e.g.
 *
 *   const res = await fetch(HOTLAP_SUBMIT_ENDPOINT, {
 *     method: 'POST',
 *     headers: { 'Content-Type': 'application/json' },
 *     body: JSON.stringify(payload),
 *   });
 *   if (!res.ok) throw new Error(`Backend responded ${res.status}`);
 *   return res.json();
 *
 * The signature (async, takes the payload, resolves the response object) is
 * already identical to the real call, so nothing else in this file changes.
 *
 * @param {HotLapSubmission} payload
 * @returns {Promise<HotLapResponse>}
 */
async function submitToBackend(payload) {
  await delay(SIMULATED_LATENCY_MS);
  return buildStubResponse(payload);
}

/* ─────────────────────── the global Unity-facing bridge ──────────────────── */

/**
 * The function Unity calls. Always returns a Promise, always resolves with a
 * valid response object, never throws — a JS exception must never bubble into
 * the Unity build.
 *
 * @param {...*} args  Unity's 8 positional args (or a string/object, defensively).
 * @returns {Promise<HotLapResponse>}
 */
async function handleSubmitRaceResult(...args) {
  try {
    const payload = normalizeSubmission(args);
    logSubmission(payload);

    // Archived week: no submission, no backend, no leaderboard. Notify the page
    // (for the on-screen notice) and return an inert response.
    if (archivedMode) {
      // eslint-disable-next-line no-console
      console.log('[SCR Weekly] Archived week — skipping submission.');
      if (archivedNoticeHandler) {
        try {
          archivedNoticeHandler({ payload });
        } catch (e) {
          /* a UI notice failure must not affect the response */
        }
      }
      return buildArchivedResponse();
    }

    return await submitToBackend(payload);
  } catch (err) {
    console.error('[SCR Weekly] SubmitRaceResult stub failed:', err);
    return buildFailureResponse((err && err.message) || 'Unexpected stub error.');
  }
}

/**
 * Install the bridge on `window`. Idempotent. Safe to call on every page mount.
 *
 * Registers BEFORE Unity ever boots (Unity only starts on the Play button, well
 * after the page mounts), so no polling and no waiting on Unity init is needed —
 * the functions simply always exist while the SCR Weekly page is open.
 */
export function installRaceResultBridge() {
  if (typeof window === 'undefined') return;

  // Global the Unity .jslib invokes on race finish.
  window.SubmitRaceResult = handleSubmitRaceResult;

  // Channel the .jslib uses to hand the response back to CompetitiveManager.
  window.UnityBridge = window.UnityBridge || {};
  window.UnityBridge.sendMessage = unityBridgeSendMessage;
}

/**
 * Optional cleanup. We deliberately do NOT delete window.SubmitRaceResult on
 * unmount by default — an in-flight race submission could still resolve — but a
 * caller may use this if it needs to fully detach.
 */
export function uninstallRaceResultBridge() {
  if (typeof window === 'undefined') return;
  setUnityInstance(null);
  setArchivedMode(false);
  setArchivedNoticeHandler(null);
}
